import { useState, useRef, useEffect } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { intelligenceApi, readStream } from '../services/api';
import { SparklesIcon, ExclamationTriangleIcon, LightBulbIcon, ChatBubbleLeftRightIcon, DocumentTextIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { useAppStore } from '../stores/appStore';

type Tab = 'health' | 'ask' | 'digest';

// ─── Roadmap Health Tab ────────────────────────────────────────────────────────
function HealthTab() {
  const data = useAppStore((s) => s.intelligenceHealth);
  const setData = useAppStore((s) => s.setIntelligenceHealth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const aiProviderLabel = useAppStore((s) => s.aiProviderLabel);
  const aiProviderModel = useAppStore((s) => s.aiProviderModel);

  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await intelligenceApi.getRoadmapHealth();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load health analysis');
    } finally {
      setLoading(false);
    }
  };

  const scoreColor = (score: number) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-amber-500';
    return 'text-red-500';
  };

  const scoreBg = (score: number) => {
    if (score >= 75) return 'bg-green-50 border-green-200';
    if (score >= 50) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const severityDot = { high: 'bg-red-500', medium: 'bg-amber-400', low: 'bg-blue-400' };

  return (
    <div className="space-y-6">
      {!data && !loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <ChartBarIcon className="w-12 h-12 text-text-tertiary" />
          <div className="text-center max-w-sm">
            <p className="text-base font-medium text-text-primary">Roadmap Health Analysis</p>
            <p className="text-sm text-text-secondary mt-1">
              {aiProviderLabel} analyzes your entire portfolio: status distribution, owner workload, dependency risks, and investment balance.
            </p>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-2 px-5 py-2.5 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-medium rounded-md transition-colors"
          >
            <SparklesIcon className="w-4 h-4" />
            Analyze Roadmap
          </button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20 gap-3 text-text-secondary">
          <svg className="animate-spin w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Analyzing your roadmap…</span>
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <p className="text-sm text-red-600">{error}</p>
          <button onClick={load} className="text-sm text-blue-600 hover:underline">Try again</button>
        </div>
      )}

      {data && (
        <div className="space-y-4">
          {/* Header: score + label + refresh */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`rounded-xl border px-5 py-3 ${scoreBg(data.analysis.healthScore)}`}>
                <span className={`text-3xl font-bold ${scoreColor(data.analysis.healthScore)}`}>
                  {data.analysis.healthScore}
                </span>
                <span className={`text-sm font-medium ml-1 ${scoreColor(data.analysis.healthScore)}`}>
                  / 100 · {data.analysis.healthLabel}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {data.cached && (
                <span className="text-xs text-text-tertiary">Cached · refreshes after 15 min</span>
              )}
              <button
                onClick={load}
                disabled={loading}
                className="flex items-center gap-1.5 text-sm border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50"
              >
                <SparklesIcon className="w-3.5 h-3.5" />
                Re-analyze
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="rounded-lg border border-border-subtle bg-surface-subtle p-4">
            <p className="text-sm text-text-primary leading-relaxed">{data.analysis.summary}</p>
            <p className="text-xs text-text-tertiary mt-2">Generated {new Date(data.generatedAt).toLocaleTimeString()}</p>
          </div>

          {/* Recommendations */}
          {data.analysis.recommendations?.length > 0 && (
            <Section
              title="Recommendations"
              icon={<LightBulbIcon className="w-4 h-4 text-amber-500" />}
              isOpen={expanded['recommendations'] !== false}
              onToggle={() => toggle('recommendations')}
            >
              <ul className="space-y-2">
                {data.analysis.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-text-primary">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Risks */}
          {data.analysis.risks?.length > 0 && (
            <Section
              title={`Risks (${data.analysis.risks.length})`}
              icon={<ExclamationTriangleIcon className="w-4 h-4 text-red-500" />}
              isOpen={!!expanded['risks']}
              onToggle={() => toggle('risks')}
            >
              <div className="space-y-2">
                {data.analysis.risks.map((risk, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${severityDot[risk.severity] || 'bg-gray-400'}`} />
                    <div>
                      <span className="font-medium text-text-primary">{risk.title}</span>
                      {risk.description && <span className="text-text-secondary"> — {risk.description}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Balance insight */}
          {data.analysis.balanceInsight && (
            <Section
              title="Investment Balance"
              icon={<ChartBarIcon className="w-4 h-4 text-blue-500" />}
              isOpen={!!expanded['balance']}
              onToggle={() => toggle('balance')}
            >
              <p className="text-sm text-text-primary">{data.analysis.balanceInsight}</p>
            </Section>
          )}

          {/* Incomplete */}
          {data.analysis.incomplete?.length > 0 && (
            <Section
              title="Incomplete / Missing Data"
              icon={<ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />}
              isOpen={!!expanded['incomplete']}
              onToggle={() => toggle('incomplete')}
            >
              <div className="space-y-2">
                {data.analysis.incomplete.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-text-primary">{item.title}</span>
                      {item.count > 0 && <span className="ml-1 text-text-tertiary">({item.count})</span>}
                      {item.description && <span className="text-text-secondary"> — {item.description}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Overloaded owners */}
          {data.analysis.overloadedOwners?.length > 0 && (
            <Section
              title="Overloaded Owners"
              icon={<ExclamationTriangleIcon className="w-4 h-4 text-orange-500" />}
              isOpen={!!expanded['owners']}
              onToggle={() => toggle('owners')}
            >
              <div className="space-y-1">
                {data.analysis.overloadedOwners.map((owner, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                    <span className="font-medium text-text-primary">{owner.name}</span>
                    <span className="text-text-secondary">{owner.detail}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Dependency risks */}
          {data.analysis.dependencyRisks?.length > 0 && (
            <Section
              title={`Dependency Risks (${data.analysis.dependencyRisks.length})`}
              icon={<ExclamationTriangleIcon className="w-4 h-4 text-purple-500" />}
              isOpen={!!expanded['deps']}
              onToggle={() => toggle('deps')}
            >
              <div className="space-y-1">
                {data.analysis.dependencyRisks.map((d, i) => (
                  <div key={i} className="text-sm text-text-primary">
                    <span className="font-medium">{d.title}</span>
                    {d.description && <span className="text-text-secondary"> — {d.description}</span>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Provider footer */}
          <p className="text-xs text-text-tertiary pt-2 border-t border-border-subtle">
            Powered by <span title={aiProviderModel || undefined} className={aiProviderModel ? 'cursor-help' : ''}>{aiProviderLabel}</span>
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Ask Tab ──────────────────────────────────────────────────────────────────
function AskTab() {
  const messages = useAppStore((s) => s.intelligenceMessages);
  const setMessages = useAppStore((s) => s.setIntelligenceMessages);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const aiProviderLabel = useAppStore((s) => s.aiProviderLabel);
  const aiProviderModel = useAppStore((s) => s.aiProviderModel);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const question = input.trim();
    setInput('');
    setError('');
    setStreaming(true);

    // Snapshot base messages at call time, then add user + empty assistant placeholder
    const baseMessages = [...messages, { role: 'user' as const, content: question }];
    setMessages([...baseMessages, { role: 'assistant' as const, content: '' }]);

    let assistantMsg = '';
    try {
      const response = await intelligenceApi.ask(question);
      for await (const chunk of readStream(response)) {
        assistantMsg += chunk;
        setMessages([...baseMessages, { role: 'assistant' as const, content: assistantMsg }]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get response');
      setMessages(baseMessages); // remove the empty assistant message
    } finally {
      setStreaming(false);
    }
  };

  const exampleQuestions = [
    'What are we shipping this quarter?',
    'Which initiatives have no owner?',
    'Give me a summary for a Slack update',
    'What is blocked right now?',
  ];

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 'calc(100vh - 280px)' }}>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <ChatBubbleLeftRightIcon className="w-10 h-10 text-text-tertiary" />
            <div className="text-center">
              <p className="text-sm font-medium text-text-primary">Ask your roadmap</p>
              <p className="text-sm text-text-secondary mt-1">{aiProviderLabel} has read all your initiative data</p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 max-w-lg">
              {exampleQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => setInput(q)}
                  className="px-3 py-1.5 text-xs text-text-secondary border border-border-default rounded-full hover:bg-surface-hover hover:text-text-primary transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1 mr-2">
                <SparklesIcon className="w-3.5 h-3.5 text-amber-600" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-surface-subtle border border-border-subtle text-text-primary'
              }`}
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {msg.content}
              {msg.role === 'assistant' && !msg.content && (
                <span className="inline-block w-2 h-4 bg-amber-400 animate-pulse ml-0.5" />
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {error && <p className="text-sm text-red-600 py-2">{error}</p>}

      {/* Input */}
      <div className="pt-3 border-t border-border-subtle space-y-1.5">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder="Ask anything about your roadmap…"
            disabled={streaming}
            className="flex-1 rounded-md border border-border-default px-3 py-2 text-sm bg-surface-default text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:border-border-focus focus:ring-border-focus/20"
          />
          <button
            onClick={send}
            disabled={!input.trim() || streaming}
            className="flex items-center gap-1.5 px-4 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed text-amber-700 text-sm font-medium rounded-md transition-colors"
          >
            <SparklesIcon className="w-4 h-4" />
            {streaming ? 'Thinking…' : 'Ask'}
          </button>
        </div>
        <p className="text-xs text-text-tertiary">
          Powered by <span title={aiProviderModel || undefined} className={aiProviderModel ? 'cursor-help' : ''}>{aiProviderLabel}</span>
        </p>
      </div>
    </div>
  );
}

// ─── Digest Tab ───────────────────────────────────────────────────────────────
function DigestTab() {
  const storeDigest = useAppStore((s) => s.intelligenceDigest);
  const setStoreDigest = useAppStore((s) => s.setIntelligenceDigest);
  const [format, setFormat] = useState<'slack' | 'executive' | 'team'>(storeDigest?.format ?? 'slack');
  const [daysBack, setDaysBack] = useState(storeDigest?.daysBack ?? 7);
  const aiProviderLabel = useAppStore((s) => s.aiProviderLabel);
  const aiProviderModel = useAppStore((s) => s.aiProviderModel);
  const output = storeDigest?.output ?? '';
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setStoreDigest(null);
    setError('');
    setGenerating(true);
    try {
      const response = await intelligenceApi.generateDigest(format, daysBack);
      let text = '';
      for await (const chunk of readStream(response)) {
        text += chunk;
        setStoreDigest({ output: text, format, daysBack });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate digest');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatLabels = {
    slack: { label: 'Chat', desc: 'Brief bullets, emoji-friendly, scannable' },
    executive: { label: 'Executive', desc: 'Formal paragraphs for stakeholders' },
    team: { label: 'Team', desc: 'Detailed with product-level grouping' },
  };

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5">Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as 'slack' | 'executive' | 'team')}
            className="rounded-md border border-border-default px-3 py-1.5 text-sm bg-surface-default text-text-primary"
          >
            {(Object.keys(formatLabels) as Array<keyof typeof formatLabels>).map((f) => (
              <option key={f} value={f}>{formatLabels[f].label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5">Period</label>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="rounded-md border border-border-default px-3 py-1.5 text-sm bg-surface-default text-text-primary"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>

        <button
          onClick={generate}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 text-amber-700 text-sm font-medium rounded-md transition-colors"
        >
          <SparklesIcon className="w-4 h-4" />
          {generating ? 'Generating…' : 'Generate Digest'}
        </button>
      </div>
      <p className="text-xs text-text-tertiary -mt-3">{formatLabels[format].desc}</p>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Output */}
      {(output || generating) && (
        <div className="relative">
          <div className="rounded-lg border border-border-subtle bg-surface-subtle p-4">
            <pre className="text-sm text-text-primary whitespace-pre-wrap font-sans leading-relaxed">
              {output}
              {generating && <span className="inline-block w-2 h-4 bg-amber-400 animate-pulse ml-0.5" />}
            </pre>
          </div>
          {output && !generating && (
            <button
              onClick={copyToClipboard}
              className="absolute top-3 right-3 px-3 py-1 text-xs rounded-md bg-white border border-border-default hover:bg-surface-hover text-text-secondary transition-colors"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          )}
        </div>
      )}

      {!output && !generating && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <DocumentTextIcon className="w-10 h-10 text-text-tertiary" />
          <div className="text-center">
            <p className="text-sm font-medium text-text-primary">Weekly Digest Generator</p>
            <p className="text-sm text-text-secondary mt-1">
              One-click summary of recent activity, ready to paste into Slack or email.
            </p>
          </div>
        </div>
      )}

      <p className="text-xs text-text-tertiary">
        Powered by <span title={aiProviderModel || undefined} className={aiProviderModel ? 'cursor-help' : ''}>{aiProviderLabel}</span>
      </p>
    </div>
  );
}

// ─── Collapsible Section ──────────────────────────────────────────────────────
function Section({
  title,
  icon,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-default overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-hover transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
          {icon}
          {title}
        </div>
        <span className="text-xs text-text-tertiary">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-1 border-t border-border-subtle">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export function IntelligencePage() {
  const aiProviderLabel = useAppStore((s) => s.aiProviderLabel);
  const aiProviderModel = useAppStore((s) => s.aiProviderModel);
  const labsFeatures = useAppStore((s) => s.labsFeatures);

  const isTabEnabled = (key: string) =>
    labsFeatures.find((f) => f.featureKey === key)?.isEnabled ?? true;

  const allTabs: Array<{ id: Tab; featureKey: string; label: React.ReactNode }> = [
    {
      id: 'health',
      featureKey: 'intelligenceHealth',
      label: (
        <span className="flex items-center gap-1.5">
          <ChartBarIcon className="w-4 h-4" />
          Health
        </span>
      ),
    },
    {
      id: 'ask',
      featureKey: 'intelligenceAsk',
      label: (
        <span className="flex items-center gap-1.5">
          <ChatBubbleLeftRightIcon className="w-4 h-4" />
          Ask
        </span>
      ),
    },
    {
      id: 'digest',
      featureKey: 'intelligenceDigest',
      label: (
        <span className="flex items-center gap-1.5">
          <DocumentTextIcon className="w-4 h-4" />
          Digest
        </span>
      ),
    },
  ];

  const tabs = allTabs.filter((t) => isTabEnabled(t.featureKey));
  const [activeTab, setActiveTab] = useState<Tab>(() => tabs[0]?.id ?? 'health');

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-text-primary">Intelligence</h1>
            <p className="text-xs text-text-secondary">
              Powered by <span title={aiProviderModel || undefined} className={aiProviderModel ? 'cursor-help' : ''}>{aiProviderLabel}</span>
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-border-subtle mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-default'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'health' && <HealthTab />}
        {activeTab === 'ask' && <AskTab />}
        {activeTab === 'digest' && <DigestTab />}
      </div>
    </div>
  );
}
